/**
 * Privacy boundary for the service worker's private photo-preview cache.
 *
 * CacheStorage is origin-wide, so the worker must know which signed-in user a
 * browser client belongs to before it may serve a cached preview. The scope is
 * a one-way digest of the account id; logout deletes every runtime cache before
 * React discards the authenticated user.
 */

const RUNTIME_PREFIX = 'family-photo-runtime-v2-';
const META_CACHE = 'family-photo-runtime-v2-clients';
let activeScope: string | null = null;

export async function activatePhotoCacheForUser(userId: string): Promise<void> {
  if (!userId || !('serviceWorker' in navigator) || !('caches' in window)) return;
  const scope = await digestScope(userId);
  activeScope = scope;

  // Account switches must not leave the previous person's previews available.
  const names = await caches.keys();
  await Promise.all(names
    .filter((name) => name.startsWith(RUNTIME_PREFIX) && name !== `${RUNTIME_PREFIX}${scope}` && name !== META_CACHE)
    .map((name) => caches.delete(name)));

  await postToWorker({ type: 'PHOTO_CACHE_SET_USER', scope });
}

export async function clearPhotoCache(): Promise<void> {
  activeScope = null;
  if ('caches' in window) {
    const names = await caches.keys();
    await Promise.all(names
      .filter((name) => name.startsWith(RUNTIME_PREFIX) || name === META_CACHE)
      .map((name) => caches.delete(name)));
  }
  await postToWorker({ type: 'PHOTO_CACHE_CLEAR_ALL' });
}

/** Re-send the client mapping when a newly activated worker takes control. */
export function repairPhotoCacheWorkerBinding(): void {
  if (!activeScope) return;
  void postToWorker({ type: 'PHOTO_CACHE_SET_USER', scope: activeScope });
}

async function digestScope(userId: string): Promise<string> {
  if (crypto.subtle) {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(userId));
    return Array.from(new Uint8Array(digest).slice(0, 16))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  // Old WebViews without SubtleCrypto still get an opaque, stable namespace.
  let hash = 2166136261;
  for (let index = 0; index < userId.length; index += 1) {
    hash ^= userId.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash >>> 0).toString(16).padStart(8, '0');
}

async function postToWorker(message: { type: string; scope?: string }): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const worker = navigator.serviceWorker.controller
    ?? (await navigator.serviceWorker.getRegistration())?.active;
  if (!worker) return;

  await new Promise<void>((resolve) => {
    const channel = new MessageChannel();
    const timeout = window.setTimeout(resolve, 1_000);
    channel.port1.onmessage = () => {
      window.clearTimeout(timeout);
      resolve();
    };
    worker.postMessage(message, [channel.port2]);
  });
}
